/**
 * Enhance Thinkspace "Seal Design" with SjImageStepper (Dev / Internal San Jose).
 * Seal Size (N/E/L) drives which seals appear and which sized icon is shown.
 */
(function () {
  'use strict';

  const OPTION_NAME = 'Seal Design';
  const SIZE_OPTION_NAME = 'Seal Size';
  const TWO_TONE_OPTION_NAME = 'Two-tone';
  const HOST_ATTR = 'data-sj-image-stepper-host';
  const ENHANCED_ATTR = 'data-sj-seal-stepper-ready';
  const ABSORBED_CLASS = 'sj-seal-size-absorbed';
  const SIZE_LETTERS = ['N', 'E', 'L'];
  // Thinkspace code vs Marvin filename (FEIK School vs FIEK*.png)
  const CODE_ALIASES = { FEIK: 'FIEK' };

  function aliasedCode(code) {
    const u = String(code || '').toUpperCase();
    if (CODE_ALIASES[u]) return CODE_ALIASES[u];
    const m = u.match(/^(.*)([NEL])$/);
    if (m && CODE_ALIASES[m[1]]) return CODE_ALIASES[m[1]] + m[2];
    return u;
  }

  function cfg() {
    return window.__SJ_IMAGE_STEPPER_CONFIG__ || {};
  }

  function sealImageMap() {
    return (cfg().sealImages && typeof cfg().sealImages === 'object' && cfg().sealImages) || {};
  }

  function normalizeSizeLetter(value) {
    const v = String(value || '').trim().toUpperCase();
    if (SIZE_LETTERS.includes(v)) return v;
    // Labels sometimes leak through — map common text
    if (/12\s*x\s*10|oval|12by10|12\s*by\s*10/i.test(String(value || ''))) return 'L';
    if (/12\s*mm/i.test(String(value || ''))) return 'E';
    if (/10\s*mm/i.test(String(value || ''))) return 'N';
    return '';
  }

  function getSealSizeSelect() {
    const field = document.querySelector(`[data-option="${SIZE_OPTION_NAME}"]`);
    return field ? field.querySelector('select') : null;
  }

  function currentSizeLetter() {
    const select = getSealSizeSelect();
    if (!select) return 'N';
    return normalizeSizeLetter(select.value) || 'N';
  }

  /** code -> Set of shape letters from Thinkspace schools_data on the page */
  function buildSealShapeIndex() {
    const out = new Map();
    const script = document.querySelector('x-3d-product-options script[data-prop="data"]');
    if (!script) return out;
    let data;
    try {
      data = JSON.parse(script.textContent || '{}');
    } catch (_) {
      return out;
    }
    const schools = data.schools_data || [];
    for (const entry of schools) {
      const school = Array.isArray(entry) ? entry[1] : entry;
      const variants = (school && school.variants) || [];
      for (const variant of variants) {
        const code = String((variant && variant.code) || '')
          .trim()
          .toUpperCase();
        if (!code) continue;
        const shapes = new Set();
        for (const sh of (variant && variant.shapes) || []) {
          const letter = normalizeSizeLetter(sh && (sh.value || sh));
          if (letter) shapes.add(letter);
        }
        if (!shapes.size) {
          // Unknown → allow all so we don't empty the strip
          SIZE_LETTERS.forEach((l) => shapes.add(l));
        }
        out.set(code, shapes);
      }
    }
    return out;
  }

  let shapeIndex = null;
  function sealShapeIndex() {
    if (!shapeIndex) shapeIndex = buildSealShapeIndex();
    return shapeIndex;
  }

  function sealSupportsSize(code, sizeLetter) {
    const shapes = sealShapeIndex().get(String(code || '').trim().toUpperCase());
    if (!shapes) return true; // unknown seal — keep visible
    return shapes.has(sizeLetter);
  }

  function lookupSealImage(value, preferredSize) {
    const map = sealImageMap();
    const raw = String(value || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const mapped = aliasedCode(upper);
    const prefer = normalizeSizeLetter(preferredSize) || 'N';
    const sizeOrder = [prefer, ...SIZE_LETTERS.filter((l) => l !== prefer)];

    const candidates = [upper, mapped, raw, raw.toLowerCase()];
    if (/[NEL]$/.test(upper) && upper.length > 1) {
      candidates.push(upper.slice(0, -1));
      candidates.push(aliasedCode(upper.slice(0, -1)));
    }
    for (const suf of sizeOrder) {
      candidates.push(upper + suf);
      candidates.push(mapped + suf);
    }

    for (const key of candidates) {
      if (!key) continue;
      if (map[key]) return map[key];
      const up = String(key).toUpperCase();
      if (map[up]) return map[up];
    }
    for (const suf of sizeOrder) {
      const want = [upper + suf, mapped + suf];
      const hit = Object.keys(map).find((k) => want.includes(String(k).toUpperCase()));
      if (hit) return map[hit];
    }
    return '';
  }

  function readSelectOptions(select, sizeLetter) {
    const size = normalizeSizeLetter(sizeLetter) || currentSizeLetter();
    return Array.from(select.options || [])
      .filter((opt) => !opt.disabled && String(opt.value || '').trim() !== '')
      .filter((opt) => sealSupportsSize(opt.value, size))
      .map((opt) => {
        const value = String(opt.value).trim();
        const label = String(opt.textContent || value).trim() || value;
        return { value, label, imageUrl: lookupSealImage(value, size) };
      });
  }

  function setSelectValue(select, value) {
    if (!select) return;
    if (select.value !== value) {
      select.value = value;
    }
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function isEngravingMislabelledField(field) {
    // Thinkspace reuses data-option="Two-tone" for the engraving checkbox markup.
    const text = String((field && field.textContent) || '').toLowerCase();
    return text.includes('engrav');
  }

  function getTwoToneField() {
    const fields = document.querySelectorAll(`[data-option="${TWO_TONE_OPTION_NAME}"]`);
    for (const field of fields) {
      if (isEngravingMislabelledField(field)) continue;
      return field;
    }
    return null;
  }

  function getTwoToneCheckbox() {
    const field = getTwoToneField();
    return field ? field.querySelector('input[type="checkbox"]') : null;
  }

  /** Thinkspace only mounts Two-tone when shape.twoTone !== "none". */
  function twoToneAvailable() {
    return Boolean(getTwoToneCheckbox());
  }

  function twoToneForced() {
    const field = getTwoToneField();
    if (!field) return false;
    if (field.querySelector('input[type="checkbox"][disabled]')) return true;
    // Thinkspace shows "Only available in 2-tone" when forced
    return /only available in 2-?tone/i.test(field.textContent || '');
  }

  function twoToneSelected() {
    const cb = getTwoToneCheckbox();
    if (cb) return Boolean(cb.checked);
    const field = getTwoToneField();
    if (!field) return false;
    const holder = field.querySelector('[data-value]');
    return (holder && holder.getAttribute('data-value') === 'true') || false;
  }

  function twoTonePriceLabel() {
    // Prefer live Thinkspace addon label if present (+$275)
    const field = getTwoToneField();
    if (field) {
      const live = (field.textContent || '').match(/\+\s*\$?\s*[\d,]+(?:\.\d{2})?/);
      if (live) return live[0].replace(/\s+/g, '');
    }
    try {
      const script = document.querySelector('x-3d-product-options script[data-prop="data"]');
      const data = script ? JSON.parse(script.textContent || '{}') : null;
      const raw = data && data.addons_pricing && data.addons_pricing.two_tone;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) {
        return `+$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
      }
    } catch (_) {
      /* ignore */
    }
    return '';
  }

  function setTwoToneSelected(on) {
    const cb = getTwoToneCheckbox();
    if (!cb || cb.disabled) return;
    if (Boolean(cb.checked) === Boolean(on)) return;
    // Native click keeps Thinkspace/Svelte twoToneSelected + pricing in sync
    cb.click();
  }

  function sizeChipCopy(value, text) {
    const letter = normalizeSizeLetter(value);
    if (letter === 'N') return { title: '10mm', subtitle: 'Small round' };
    if (letter === 'E') return { title: '12mm', subtitle: 'Large round' };
    if (letter === 'L') return { title: '12×10mm', subtitle: 'Oval' };
    const raw = String(text || '')
      .trim()
      .replace(/seal\s*size:?/i, '')
      .trim();
    return { title: raw || letter || value, subtitle: '' };
  }

  function availableSizeOptions() {
    const select = getSealSizeSelect();
    if (!select) return [];
    return Array.from(select.options || [])
      .filter((opt) => !opt.disabled && String(opt.value || '').trim() !== '')
      .map((opt) => {
        const value = String(opt.value).trim();
        const letter = normalizeSizeLetter(value);
        const copy = sizeChipCopy(value, opt.textContent);
        return {
          value,
          letter: letter || value.toUpperCase(),
          title: copy.title,
          subtitle: copy.subtitle,
        };
      })
      .filter((opt) => SIZE_LETTERS.includes(opt.letter));
  }

  function renderSizeChips(stepper) {
    if (!stepper || typeof stepper.footerExtraEl !== 'function') return;
    const host = stepper.footerExtraEl();
    if (!host) return;

    const sizes = availableSizeOptions();
    const current = currentSizeLetter();
    if (stepper.root) {
      stepper.root.setAttribute('data-seal-size', current || '');
    }

    host.innerHTML = '';
    if (!sizes.length && !twoToneAvailable()) return;

    const wrap = document.createElement('div');
    wrap.className = 'sj-seal-size-chips';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Seal size and finish');

    if (twoToneAvailable()) {
      const on = twoToneSelected();
      const forced = twoToneForced();
      const price = twoTonePriceLabel();
      const status = forced ? 'Yes' : on ? 'Yes' : 'Off';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sj-seal-size-chip sj-seal-size-chip--two-tone';
      btn.classList.toggle('is-selected', on);
      btn.classList.toggle('is-on', on);
      btn.disabled = forced && on;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute(
        'aria-label',
        price
          ? `Two-tone seal: ${status}${on ? `, ${price}` : ''}`
          : `Two-tone seal: ${status}`
      );
      btn.innerHTML =
        `<span class="sj-seal-size-chip__text">` +
        `<span class="sj-seal-size-chip__title">Two-tone Seal</span>` +
        `<span class="sj-seal-size-chip__subtitle">${status}${
          on && price ? ` · ${price}` : ''
        }</span>` +
        `</span>`;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (forced) return;
        setTwoToneSelected(!twoToneSelected());
        // Optimistic paint; Thinkspace pricing/viewer catch up via their bindings
        window.setTimeout(() => refreshAllSealSteppers({ keepWindow: true }), 0);
      });
      wrap.appendChild(btn);
    }

    sizes.forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sj-seal-size-chip';
      btn.classList.toggle('is-selected', opt.letter === current);
      btn.setAttribute('data-seal-size', opt.letter);
      btn.setAttribute('aria-pressed', opt.letter === current ? 'true' : 'false');
      btn.setAttribute(
        'aria-label',
        opt.subtitle ? `${opt.title}, ${opt.subtitle}` : opt.title
      );
      btn.innerHTML =
        `<span class="sj-seal-size-chip__text">` +
        `<span class="sj-seal-size-chip__title">${opt.title}</span>` +
        (opt.subtitle
          ? `<span class="sj-seal-size-chip__subtitle">${opt.subtitle}</span>`
          : '') +
        `</span>`;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sizeSelect = getSealSizeSelect();
        if (!sizeSelect) return;
        if (sizeSelect.value !== opt.value) {
          sizeSelect.value = opt.value;
        }
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        // Immediate UI feedback even if Thinkspace is slow to echo
        refreshAllSealSteppers({ keepWindow: true });
      });
      wrap.appendChild(btn);
    });

    host.appendChild(wrap);
  }

  function absorbSealOptionFields() {
    const sizeField = document.querySelector(`[data-option="${SIZE_OPTION_NAME}"]`);
    if (sizeField) sizeField.classList.add(ABSORBED_CLASS);
    const twoToneField = getTwoToneField();
    if (twoToneField) twoToneField.classList.add(ABSORBED_CLASS);
  }

  function refreshStepper(field, { keepWindow } = {}) {
    const stepper = field && field._sjSealStepper;
    const select = field && field.querySelector('select');
    if (!stepper || !select) return;
    const size = currentSizeLetter();
    const next = readSelectOptions(select, size);
    const prevKey = stepper.options.map((o) => `${o.value}:${o.imageUrl}`).join('|');
    const nextKey = next.map((o) => `${o.value}:${o.imageUrl}`).join('|');
    if (prevKey !== nextKey) {
      const selected = String(select.value || '');
      stepper.setOptions(next);
      // If current seal isn't valid for this size, Thinkspace may change it —
      // pin window to whatever the select says after a tick.
      if (selected && !next.some((o) => o.value === selected)) {
        window.setTimeout(() => {
          stepper.syncFromValue(undefined, { keepWindow: false });
        }, 0);
      } else if (keepWindow) {
        stepper.syncFromValue(undefined, { keepWindow: true });
      }
    } else {
      stepper.syncFromValue(undefined, { keepWindow: keepWindow !== false });
    }
    renderSizeChips(stepper);
    absorbSealOptionFields();
  }

  let sizeWiringReady = false;

  function refreshAllSealSteppers(opts) {
    document.querySelectorAll(`[data-option="${OPTION_NAME}"][${ENHANCED_ATTR}="1"]`).forEach((field) => {
      refreshStepper(field, opts);
    });
  }

  function wireSealSize() {
    if (sizeWiringReady) return;
    sizeWiringReady = true;

    const onSizeMaybeChanged = () => {
      refreshAllSealSteppers({ keepWindow: true });
    };

    // Direct select / checkbox change (Seal Size + Two-tone seal — not engraving)
    document.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (!(t instanceof HTMLElement)) return;
        if (t.closest(`[data-option="${SIZE_OPTION_NAME}"]`)) {
          onSizeMaybeChanged();
          return;
        }
        const twoToneHost = t.closest(`[data-option="${TWO_TONE_OPTION_NAME}"]`);
        if (twoToneHost && !isEngravingMislabelledField(twoToneHost)) {
          onSizeMaybeChanged();
        }
      },
      true
    );

    // Thinkspace often rewrites Seal Size / Two-tone without a clean change event.
    const bindAbsorbedField = (optionName, flag) => {
      const field =
        optionName === TWO_TONE_OPTION_NAME
          ? getTwoToneField()
          : document.querySelector(`[data-option="${optionName}"]`);
      if (!field || field[flag]) return;
      const mo = new MutationObserver(onSizeMaybeChanged);
      mo.observe(field, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'selected', 'checked', 'data-value', 'data-value-text', 'disabled'],
      });
      field[flag] = mo;
      field.classList.add(ABSORBED_CLASS);
    };
    const revealEngravingIfHidden = () => {
      document.querySelectorAll(`[data-option="${TWO_TONE_OPTION_NAME}"]`).forEach((field) => {
        if (!isEngravingMislabelledField(field)) return;
        if (!field.classList.contains(ABSORBED_CLASS)) return;
        field.classList.remove(ABSORBED_CLASS);
      });
    };
    let bindScheduled = false;
    const bindAll = () => {
      bindAbsorbedField(SIZE_OPTION_NAME, '_sjSealSizeMo');
      bindAbsorbedField(TWO_TONE_OPTION_NAME, '_sjTwoToneMo');
      revealEngravingIfHidden();
    };
    const scheduleBindAll = () => {
      if (bindScheduled) return;
      bindScheduled = true;
      requestAnimationFrame(() => {
        bindScheduled = false;
        bindAll();
      });
    };
    bindAll();
    // Prefer widget-scoped watch (shared with other enhancers); fall back once.
    const widget =
      document.querySelector('x-3d-product-options-custom-wrapper x-3d-product-options') ||
      document.querySelector('x-3d-product-options');
    if (widget && window.Sj3dBuilder && typeof window.Sj3dBuilder.watchWidget === 'function') {
      window.Sj3dBuilder.watchWidget(widget, scheduleBindAll);
    } else if (widget) {
      const moScan = new MutationObserver(scheduleBindAll);
      moScan.observe(widget, { childList: true, subtree: true });
    }
  }

  function enhanceField(field) {
    if (!field || field.getAttribute(ENHANCED_ATTR) === '1') return;
    const select = field.querySelector('select');
    if (!select || typeof window.SjImageStepper !== 'function') return;

    // Lazily build once; do not rebuild on every step click
    sealShapeIndex();

    const opts = readSelectOptions(select, currentSizeLetter());

    field.setAttribute(ENHANCED_ATTR, '1');
    field.setAttribute(HOST_ATTR, 'seal');

    const mount = document.createElement('div');
    mount.className = 'sj-image-stepper-mount';
    field.appendChild(mount);

    const stepper = new window.SjImageStepper({
      mountEl: mount,
      kind: 'seal',
      layout: 'strip',
      visibleCount: 3,
      ariaLabel: 'Seal Design',
      options: opts,
      getValue: () => String(select.value || ''),
      setValue: (value) => setSelectValue(select, value),
    });

    field._sjSealStepper = stepper;
    renderSizeChips(stepper);
    absorbSealOptionFields();

    const mo = new MutationObserver(() => {
      refreshStepper(field, { keepWindow: true });
    });
    mo.observe(select, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'selected'] });
    select.addEventListener('change', () => {
      stepper.syncFromValue(undefined, { keepWindow: false });
      renderSizeChips(stepper);
    });

    field._sjSealStepperMo = mo;
    wireSealSize();
  }

  function scan(root) {
    const scope = root || document;
    scope.querySelectorAll(`[data-option="${OPTION_NAME}"]`).forEach(enhanceField);
  }

  function boot() {
    const B = window.Sj3dBuilder;
    const widget =
      (B && B.widgetEl && B.widgetEl()) ||
      document.querySelector('x-3d-product-options-custom-wrapper x-3d-product-options') ||
      document.querySelector('x-3d-product-options');
    if (!widget) return;

    const scanAll = () => {
      // Keep cached shapeIndex — schools JSON does not change during a session
      scan(widget);
    };

    if (B && typeof B.watchWidget === 'function') {
      B.watchWidget(widget, scanAll);
    } else {
      scanAll();
      const mo = new MutationObserver(() => {
        window.requestAnimationFrame(scanAll);
      });
      mo.observe(widget, { childList: true, subtree: true });
      widget.addEventListener('widget:loaded', scanAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
